import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { db } from '../firebase';
import { collection, addDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { FaPlus, FaImage, FaAngleRight, FaHeart, FaFolder, FaArrowLeft } from 'react-icons/fa';
import './ProGallerySelector.css';

const ProGallerySelector = ({ setActiveComponent }) => {
    const [folders, setFolders] = useState([]);
    const [galleries, setGalleries] = useState([]);
    const [currentView, setCurrentView] = useState('folders'); // 'folders' or 'galleries'
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [showAddFolderModal, setShowAddFolderModal] = useState(false);
    const [showAddGalleryModal, setShowAddGalleryModal] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newGalleryName, setNewGalleryName] = useState('');
    const [newGalleryId, setNewGalleryId] = useState('');
    const [selectedFolderId, setSelectedFolderId] = useState('');
    const { isAdmin, user } = useContext(AuthContext);

    // Fetch folders from Firestore with real-time updates
    useEffect(() => {
        const foldersRef = collection(db, 'gallery_folders');
        const unsubscribe = onSnapshot(query(foldersRef, orderBy('order', 'asc')), (snapshot) => {
            const foldersData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setFolders(foldersData);
        });

        return () => unsubscribe();
    }, []);

    // Fetch galleries from Firestore with real-time updates
    useEffect(() => {
        const galleriesRef = collection(db, 'gallery_config');
        const unsubscribe = onSnapshot(galleriesRef, (snapshot) => {
            const galleriesData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                totalLikes: doc.data().totalLikes || 0
            }));
            setGalleries(galleriesData);
        });

        return () => unsubscribe();
    }, []);

    // Helper function to get galleries for a specific folder
    const getGalleriesForFolder = (folderId) => {
        return galleries.filter(gallery => gallery.folderId === folderId);
    };

    // Helper function to get folder stats
    const getFolderStats = (folderId) => {
        const folderGalleries = getGalleriesForFolder(folderId);
        const galleryCount = folderGalleries.length;
        const totalLikes = folderGalleries.reduce((sum, gallery) => sum + (gallery.totalLikes || 0), 0);
        return { galleryCount, totalLikes };
    };

    const handleFolderSelect = (folder) => {
        setSelectedFolder(folder);
        setCurrentView('galleries');
    };

    const handleGallerySelect = (galleryName) => {
        setActiveComponent(galleryName);
    };

    const handleBackToFolders = () => {
        setCurrentView('folders');
        setSelectedFolder(null);
    };

    const handleAddFolder = async (e) => {
        e.preventDefault();
        if (!newFolderName) return;

        try {
            await addDoc(collection(db, 'gallery_folders'), {
                name: newFolderName,
                description: '',
                createdAt: new Date(),
                order: folders.length
            });

            setNewFolderName('');
            setShowAddFolderModal(false);
        } catch (error) {
            console.error('Error adding folder:', error);
        }
    };

    const handleAddGallery = async (e) => {
        e.preventDefault();
        if (!newGalleryName || !newGalleryId || !selectedFolderId) return;

        try {
            await addDoc(collection(db, 'gallery_config'), {
                name: newGalleryName,
                collectionName: `gallery_${newGalleryId.toLowerCase().replace(/\s+/g, '_')}`,
                folderId: selectedFolderId,
                totalLikes: 0,
                order: getGalleriesForFolder(selectedFolderId).length
            });

            setNewGalleryName('');
            setNewGalleryId('');
            setSelectedFolderId('');
            setShowAddGalleryModal(false);
        } catch (error) {
            console.error('Error adding gallery:', error);
        }
    };

    // Render folder view
    const renderFolderView = () => (
        <>
            <h2>Pro Gallery Folders</h2>
            <p className="gallery-intro">Select a folder to view galleries inside</p>

            <div className="folder-mosaic">
                {folders.map(folder => {
                    const stats = getFolderStats(folder.id);
                    return (
                        <div
                            key={folder.id}
                            className="folder-tile"
                            onClick={() => handleFolderSelect(folder)}
                        >
                            <div className="folder-tile-icon"><FaFolder /></div>
                            <div className="folder-tile-name">{folder.name}</div>
                            <div className="folder-tile-stats">
                                <span>{stats.galleryCount} {stats.galleryCount !== 1 ? 'galleries' : 'gallery'}</span>
                                <span className="folder-tile-dot">·</span>
                                <FaHeart className="folder-tile-heart" />
                                <span>{stats.totalLikes}</span>
                            </div>
                        </div>
                    );
                })}

                {isAdmin && (
                    <>
                        <div
                            className="folder-tile folder-tile--add-folder"
                            onClick={() => setShowAddFolderModal(true)}
                        >
                            <div className="folder-tile-icon"><FaPlus /></div>
                            <div className="folder-tile-name">New Folder</div>
                        </div>
                        <div
                            className="folder-tile folder-tile--add-gallery"
                            onClick={() => setShowAddGalleryModal(true)}
                        >
                            <div className="folder-tile-icon"><FaImage /></div>
                            <div className="folder-tile-name">New Gallery</div>
                        </div>
                    </>
                )}
            </div>
        </>
    );

    // Render gallery view
    const renderGalleryView = () => {
        const folderGalleries = selectedFolder ? getGalleriesForFolder(selectedFolder.id) : galleries;
        
        return (
            <>
                <div className="gallery-header">
                    <button className="back-button" onClick={handleBackToFolders}>
                        <FaArrowLeft /> Back
                    </button>
                    <h2>{selectedFolder?.name || 'All Galleries'}</h2>
                </div>
                <p className="gallery-intro">Select a gallery to view photos</p>
                
                <div className="gallery-grid">
                    {folderGalleries.map(gallery => (
                        <div 
                            key={gallery.id}
                            className="gallery-card" 
                            onClick={() => handleGallerySelect(gallery.collectionName)}
                        >
                            <div className="gallery-icon"><FaImage /></div>
                            <h3>{gallery.name}</h3>
                            <div className="gallery-info">
                                <FaHeart className="like-icon" /> {gallery.totalLikes || 0} <FaAngleRight className="arrow-icon" />
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    return (
        <div className="pro-gallery-selector">
            {currentView === 'folders' ? renderFolderView() : renderGalleryView()}
            
            {showAddFolderModal && (
                <div className="modal-overlay" onClick={() => setShowAddFolderModal(false)}>
                    <div className="add-folder-modal" onClick={e => e.stopPropagation()}>
                        <h3>Add New Folder</h3>
                        <form onSubmit={handleAddFolder}>
                            <input
                                type="text"
                                placeholder="Folder Name"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                required
                            />
                            <div className="modal-buttons">
                                <button type="button" onClick={() => setShowAddFolderModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit">
                                    Add Folder
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showAddGalleryModal && (
                <div className="modal-overlay" onClick={() => setShowAddGalleryModal(false)}>
                    <div className="add-gallery-modal" onClick={e => e.stopPropagation()}>
                        <h3>Add New Gallery</h3>
                        <form onSubmit={handleAddGallery}>
                            <input
                                type="text"
                                placeholder="Gallery Display Name"
                                value={newGalleryName}
                                onChange={(e) => setNewGalleryName(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Gallery ID (e.g., winter_shoot)"
                                value={newGalleryId}
                                onChange={(e) => setNewGalleryId(e.target.value)}
                                required
                            />
                            <select
                                value={selectedFolderId}
                                onChange={(e) => setSelectedFolderId(e.target.value)}
                                required
                            >
                                <option value="">Select a folder</option>
                                {folders.map(folder => (
                                    <option key={folder.id} value={folder.id}>
                                        {folder.name}
                                    </option>
                                ))}
                            </select>
                            <div className="modal-buttons">
                                <button type="button" onClick={() => setShowAddGalleryModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit">
                                    Add Gallery
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProGallerySelector; 